import { useCallback, useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import update from 'immutability-helper';
import styled from 'styled-components';
import { useProcContext } from '@/entities/proc';
import { useProcManageActions } from '@/features/proc-management';
import {
  ProcDetailsResponse,
  StepItemResponse as StepItemType,
} from '@/shared/api';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { EventTypes } from '@/shared/types';
import { trigger } from '@/shared/utils';
import { ProcStepItem } from './ProcStepItem';

export const ProcStepsSidebar = () => {
  const { theme } = useThemeContext();
  const { proc, setSelectedStep, setSelectedComponent } = useProcContext();

  const [steps, setSteps] = useState(proc?.stepData.steps || []);

  const { addStep, addStepLoading, editProc } = useProcManageActions();

  const handleStepAdd = async () => {
    addStep({
      title: `Step ${Number(proc?.stepData?.steps?.length) + 1}`,
      onSuccess(data) {
        const addedStep = data?.stepData.steps[data.stepData.steps.length - 1];
        setSelectedStep(addedStep);
        setSelectedComponent(undefined);
        setTimeout(() => {
          trigger(EventTypes.STEP_ADDED, { step: addedStep });
        }, 50);
      },
    });
  };

  const moveStepItem = useCallback((dragIndex: number, hoverIndex: number) => {
    setSteps((prevSteps: StepItemType[]) =>
      update(prevSteps, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevSteps[dragIndex] as StepItemType],
        ],
      }),
    );
  }, []);

  const handleItemDrop = async () => {
    const { title, description, tags, imageLink, uploadedImageLink } =
      proc as ProcDetailsResponse;
    editProc({
      data: {
        title,
        description,
        tags,
        imageLink,
        uploadedFileKey: uploadedImageLink,
        stepData: { steps: steps.map((step) => step.id) },
      },
    });
  };

  useEffect(() => {
    setSteps(proc?.stepData.steps || []);
  }, [proc?.stepData.steps]);

  return (
    <SidebarWrapper>
      <SidebarHeader>
        <div style={{ color: themeConfig[theme].colors.text }}>Steps</div>
        <Tooltip title="Add new step" mouseEnterDelay={0.5}>
          <Button
            data-testid="add-step-button"
            icon={<PlusOutlined />}
            size="small"
            loading={addStepLoading}
            onClick={handleStepAdd}
          />
        </Tooltip>
      </SidebarHeader>
      <SidebarLeftContent
        data-testid="steps"
        style={{ borderTopColor: themeConfig[theme].colors.border }}
      >
        {steps.map((step: StepItemType, index: number) => (
          <ProcStepItem
            key={step?.id}
            step={step}
            index={index}
            moveStepItem={moveStepItem}
            onItemDrop={handleItemDrop}
          />
        ))}
      </SidebarLeftContent>
    </SidebarWrapper>
  );
};

const SidebarWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-height: calc(100vh - 50px);
  flex-direction: column;
  padding-left: 3px;
  overflow-y: hidden;
`;

const SidebarHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  padding: 15px;
`;

const SidebarLeftContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-top: 1px solid;
  overflow-y: auto;
  padding-bottom: 20px;
`;
